import { Injectable, Type } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FilterableField } from 'src/shared/models/pipeline-config/reporting-sets.models';
import { ModuleManager } from '../../../shared/models/module-manager';
import { CVApiService } from '../services/cv-api.service';
import { FilterMetricsParams, MetricSelectorData, SelectedMetric, SelectorFilterMetric, SelectorMetric } from './metric-selector.model';

@Injectable({
    providedIn: 'root'
})
export class MetricSelectorService {

    public moduleKey;
    public moduleId;
    public maxMetrics: number;
    public selectedMetrics;
    public metricType;
    public includeAttMetrics: boolean;
    public allMetrics: SelectorMetric[];
    public allMetricsLoaded = false; //for metric formula
    /*whether the metric selector modal opened was for dashboards, this is for custom metric names on dashboards*/
    public canRenameMetrics: boolean = false; 
    public singleMetric: boolean = false; //for when there is only one metric that needs to be selected
    public disabledNumericMetrics: number[];  //Number array that indicates which numeric metric types should be ignored
    //any filters needed to filter out metrics on the metrics pull
    //currently used in gamification to filter out metrics that are not applicable
    public filterParams: FilterMetricsParams = null; 
    groupTypes: any;
    private creatorId: number; //only used for wallboards
    public hideMetricFilter: boolean = false;
    public enableCategory: boolean = false;

    constructor(
        private cvApiService: CVApiService,
        private modalService: NgbModal,
    ) { }

    public openModal(modalComponent: Type<any>, moduleManager: ModuleManager, selectedMetrics, canRenameMetrics = false): NgbModalRef {
        const moduleInfo = moduleManager.getModuleInfo();
        this.selectedMetrics = selectedMetrics;
        this.moduleKey = moduleManager.getModuleKey();
        this.moduleId = moduleInfo.ModuleID;
        this.maxMetrics = moduleInfo.DTO.MaxMetricCount;
        this.includeAttMetrics = moduleInfo.DTO.IncludeUserAttMetrics;
        this.canRenameMetrics = canRenameMetrics;
        //Numeric metric types that the widget will ignore
        this.disabledNumericMetrics = moduleManager.moduleSettings.disabledNumericMetrics;
        
        const modalRef = this.modalService.open(modalComponent, {
            backdrop: 'static',
            keyboard: false,
            windowClass: 'metricSelectorWindow',
            modalDialogClass: 'metricSelectorModal',
            backdropClass: 'metricSelectorModalBackdrop'
        });

        return modalRef;
    }

    public openModalForWallboard(modalComponent: Type<any>, key, id, selectedMetrics, creatorId, isSlideout = false): NgbModalRef {
        let existingMetrics = [];
        this.canRenameMetrics = true;

        if (!isSlideout) {
            selectedMetrics?.forEach(m => {
                existingMetrics.push({
                    Id: m.mID,
                    Abbreviation: m.mAbr,
                    MetricName: m.mName,
                    DefaultDirection: m.mDir,
                    DisplayName: m.DisplayName,
                    ConversionType: m.mCT,
                });
            });
        }

        this.selectedMetrics = (isSlideout ? selectedMetrics : existingMetrics) ?? [];
        this.moduleKey = key;
        this.moduleId = id;
        this.creatorId = creatorId;

        const modalRef = this.modalService.open(modalComponent, {
            backdrop: 'static',
            keyboard: false,
            windowClass: 'wallboardMetricSelectorWindow',
            modalDialogClass: 'wallboardMetricSelectorModal',
            backdropClass: 'wallboardMetricSelectorModal'
        });

        return modalRef;
    }

    public openModalForSingleMetric(modalComponent: Type<any>, selectedMetric) {
        let existingMetrics = [];
        this.singleMetric = true;
        this.maxMetrics = 1;

        if (!!selectedMetric) {
            existingMetrics.push({
                Id: selectedMetric.mID,
                Abbreviation: selectedMetric.mAbr,
                MetricName: selectedMetric.mName,
                DefaultDirection: selectedMetric.mDir,
                DisplayName: selectedMetric.DisplayName,
                ConversionType: selectedMetric.mCT,
            });
        }

        this.selectedMetrics = existingMetrics ?? [];

        const modalRef = this.modalService.open(modalComponent, {
            backdrop: 'static',
            keyboard: false,
            windowClass: 'metricSelectorWindow',
            modalDialogClass: 'metricSelectorModal',
            backdropClass: 'metricSelectorModalBackdrop'
        });

        return modalRef;
    }

    public openModalForMultpleleMetric(modalComponent: Type<any>, selectedMetric, maxMetrics: number = 10) {
        let existingMetrics = [];
        this.singleMetric = true;
        this.maxMetrics = maxMetrics;
        if (!!selectedMetric) {
            selectedMetric?.forEach(m => {
                existingMetrics.push({
                    Id: m.metricID,
                    Abbreviation: m.abbrev,
                    MetricName: m.MetricName,
                    DefaultDirection: m.mDir,
                    DisplayName: m.MetricName,
                    ConversionType: m.ConversionType,
                });
            });
        }

        this.selectedMetrics = existingMetrics ?? [];

        const modalRef = this.modalService.open(modalComponent, {
            backdrop: 'static',
            keyboard: false,
            windowClass: 'metricSelectorWindow',
            modalDialogClass: 'metricSelectorModal',
            backdropClass: 'metricSelectorModalBackdrop'
        });

        return modalRef;
    }

    public async getMetricSelectorData() {
        try {
            let url = `/api/v1/metric-selector/${this.moduleId}/${this.moduleKey}`;
            if (this.includeAttMetrics)
                url += `?includeAttMetrics=${this.includeAttMetrics}`;
            if (this.creatorId)
                url += `?creatorId=${this.creatorId}`;
            if (this.filterParams) {
                let filterString = new URLSearchParams(this.filterParams as any).toString();
                url += `?${filterString}`;
            }
            const response = await this.cvApiService.get<MetricSelectorData>(url);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed getting data for Metric Selector");
            throw err;
        }
    }

    public async getAllMetrics() {
        if (this.allMetricsLoaded)
            return;
        try {
            const url = `/api/v1/metric-selector/all-metrics`;
            const response = await this.cvApiService.get<SelectorMetric[]>(url);
            if (response.status_code >= 200 && response.status_code < 300)
                this.allMetrics = response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to get all metrics");
            throw err;
        }
        finally {
            this.allMetricsLoaded = true;
        }
    }

    public async updateSelectedMetrics(metrics: SelectedMetric[], lastSelectedMetric: number = null) {
        if (!this.singleMetric) {
            const pageName = this.moduleKey;
            try {
                let url = `/api/v1/metric-selector/${pageName}`;
                if (this.creatorId) {
                    url += `?creatorId=${this.creatorId}`;
                }
                if(lastSelectedMetric){
                    const char = url.includes('?') ? '&' : '?';
                    url += `${char}lastSelected=${lastSelectedMetric}`;
                }
                const response = await this.cvApiService.put(url, metrics);
                if (response.status_code >= 200 && response.status_code < 300)
                    return response.data;
                else throw response;
            } catch (err) {
                console.error("Failed to update selected metrics");
                throw err;
            }
        }

    }

    public async updateFavorites(metricId: number) {
        try {
            const url = `/api/v1/metric-selector/favorites`;
            const response = await this.cvApiService.put(url, metricId);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to update favorite metrics");
            throw err;
        }
    }

    public async updateFavDropdown(isExpanded: boolean) {
        try {
            const url = `/api/v1/metric-selector/favorites/${isExpanded}`;
            const response = await this.cvApiService.put(url);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to update favorite dropdown");
            throw err;
        }
    }

    async updateCustomMetricNames(metric, dashboardKey: string, metricCustomName: string) {
        try {
            const url = `/api/v1/metric-selector/customnames`;
            const bodyObj = {MetricId: metric.MetricID, MetricName: metric.Abbreviation, MetricCustomName: metricCustomName, DashboardKey:dashboardKey};
            const response = await this.cvApiService.put(url, bodyObj);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to update custom metric names");
            throw err;
        }
    }
    
    public async updateFilterMetrics(metrics: SelectorFilterMetric[]) {
        const pageName = `${this.moduleKey}`;
        try {
            const url = `/api/v1/metric-selector/filter-metrics/${pageName}/${this.creatorId}`;
            const response = await this.cvApiService.put(url, metrics);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to update metric filters");
            throw err;
        }
    }

    async getRenamePermission() {
        try {
            const url = "/api/v1/metric-selector/renamePermission";
            const response = await this.cvApiService.get<boolean>(url);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to get rename permission");
            throw err;
        }
    }

    /**
     * Gets the relevant NIC QE filterables ensuring no duplicates from the same reporting sets.
     * @returns Object containing the filterable fields and attributes.
     */
    public getQEFilterableAttributes(selectedMetrics: SelectedMetric[]): { [FieldName: string]: FilterableField } {
        const mapping: Map<string, { [FieldName: string]: FilterableField }> = new Map();

        for (const metric of selectedMetrics)
            for (const rsName in metric.QEAttributeFiltersByRSDef)
                if (Object.prototype.hasOwnProperty.call(metric.QEAttributeFiltersByRSDef, rsName) && !mapping.has(rsName))
                    mapping.set(rsName, metric.QEAttributeFiltersByRSDef[rsName]);

        let ret: { [FieldName: string]: FilterableField } = {};
        for (const [rsName, filterables] of mapping)
            ret = { ...ret, ...filterables };

        return ret;
    }
}
